import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, Eye, FileSignature, FolderOpen, Trash2, Upload, X } from "lucide-react";
import { useRef, useState, type JSX } from "react";

import { useAuth } from "../../../auth/AuthProvider.js";
import { usePermissions } from "../../../auth/usePermissions.js";
import { ConfirmDialog } from "../../../components/ui/confirm-dialog.js";
import { PageHeader } from "../../../components/ui/page-header.js";
import {
  AGREEMENT_LABELS,
  type AgreementKind,
  type AgreementSlot,
  type CompanyFile,
  agreementUrl,
  companyDocumentKeys,
  companyFileUrl,
  deleteCompanyFile,
  downloadToDisk,
  fetchBytes,
  getAgreements,
  listCompanyFiles,
  uploadCompanyFile,
} from "../../../lib/companyDocumentsApi.js";
import { date } from "../../../lib/format.js";
import { PdfInlinePreview } from "../../knowledge-base/PdfInlinePreview.js";
import { formatBytes } from "../../knowledge-base/utils.js";

/**
 * Legal — the company's OWN paperwork (2026-09-13; "Documents" until
 * 2026-09-17, when the CEO renamed it and made it visible to everyone).
 *
 * Two cards, one grant. The AGREEMENTS card is what the company signed to
 * become a reseller — the Reseller Agreement and the NDA, its own signed copy
 * and XOP's countersigned one — read off the application that was activated
 * into this company. Until this page existed a company could see those only
 * while it was still an applicant; activation killed that route and the
 * signed contract went dark. The FILES card is the company cabinet: what XOP
 * filed against the company and what the company filed itself.
 *
 * WHO SEES IT. Every signed-in member of the company: there is no read
 * permission (`documents:view` was removed on 2026-09-17), no gate on the
 * rail entry or the route, and crm-api scopes every read to the member's OWN
 * company. The page is ALWAYS there — when nothing has been signed or filed
 * yet, each card says so in words rather than the page hiding. Only
 * `documents:edit` (filing new paperwork, removing the company's OWN files)
 * is a grant; crm-api enforces it on every call, and what this page hides is
 * a courtesy.
 *
 * WHAT IT NEVER DOES. Delete XOP's paperwork: `deletable` is crm-api's verdict
 * per row and is rendered, never re-derived. A company that could shred the
 * other side's countersigned agreement would be running a shredder, not a
 * file cabinet.
 *
 * PDFs render through the shared pdf.js reader — never an iframe — and the
 * bytes come through this origin.
 */
export function DocumentsPage(): JSX.Element {
  const { user } = useAuth();
  const { can } = usePermissions();
  const canEdit = can("documents:edit");
  const resellerId = user?.resellerId ?? 0;

  const [preview, setPreview] = useState<{ title: string; url: string; key: string } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  return (
    <Box sx={{ display: "grid", gap: 3 }} data-testid="legal-page">
      <PageHeader
        title="Legal"
        description="Your agreements with XOP Networks, and your company's file cabinet."
      />

      <AgreementsCard
        onPreview={(slot) =>
          setPreview({ title: AGREEMENT_LABELS[slot.kind], url: agreementUrl(slot.kind), key: slot.kind })
        }
        onError={setNotice}
      />

      <FilesCard
        resellerId={resellerId}
        canEdit={canEdit}
        onPreview={(file) =>
          setPreview({ title: file.name, url: companyFileUrl(resellerId, file.id), key: `file:${file.id}` })
        }
        onError={setNotice}
      />

      <Dialog open={preview !== null} onClose={() => setPreview(null)} maxWidth="lg" fullWidth>
        {preview && (
          <>
            <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, pr: 6 }}>
              <FileSignature size={18} />
              <Box component="span" sx={{ flex: 1, minWidth: 0, fontSize: 15 }}>
                {preview.title}
              </Box>
              <IconButton
                aria-label="Close preview"
                onClick={() => setPreview(null)}
                sx={{ position: "absolute", right: 8, top: 8 }}
              >
                <X size={18} />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 0 }}>
              <PdfInlinePreview
                docId={0}
                filename={preview.title}
                cacheKey={["company-documents", "bytes", preview.key]}
                loader={() => fetchBytes(preview.url)}
              />
            </DialogContent>
          </>
        )}
      </Dialog>

      <Snackbar
        open={notice !== null}
        autoHideDuration={6000}
        onClose={() => setNotice(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="error" onClose={() => setNotice(null)} sx={{ width: "100%" }}>
          {notice}
        </Alert>
      </Snackbar>
    </Box>
  );
}

/* ── Agreements ──────────────────────────────────────────────────────── */

function AgreementsCard({
  onPreview,
  onError,
}: {
  onPreview: (slot: AgreementSlot) => void;
  onError: (message: string) => void;
}): JSX.Element {
  const { data, isLoading, isError } = useQuery({
    queryKey: companyDocumentKeys.agreements,
    queryFn: getAgreements,
  });

  const [busy, setBusy] = useState<AgreementKind | null>(null);
  const download = async (slot: AgreementSlot): Promise<void> => {
    setBusy(slot.kind);
    try {
      await downloadToDisk(agreementUrl(slot.kind), slot.filename ?? `${slot.kind}.pdf`);
    } catch (err) {
      onError(err instanceof Error ? err.message : "Download failed.");
    } finally {
      setBusy(null);
    }
  };

  const signed = data?.documents.some((d) => d.attached) ?? false;

  return (
    <Card data-testid="agreements-card">
      <CardContent sx={{ display: "grid", gap: 2 }}>
        <SectionTitle
          icon={<FileSignature size={18} />}
          title="Agreements with XOP Networks"
          hint={
            data?.activated_at
              ? `Reseller since ${date(data.activated_at)}.`
              : "The Reseller Agreement and Non-Disclosure Agreement your company signed, and XOP Networks' countersigned copies."
          }
        />

        {isLoading && <CircularProgress size={20} />}
        {isError && <Alert severity="error">The agreements could not be loaded.</Alert>}

        {data && !signed && (
          <Box data-testid="agreements-empty" sx={emptyBoxSx}>
            No agreements were signed through the reseller portal. If your company was set up directly by
            XOP Networks, your paperwork is held by your XOP contact.
          </Box>
        )}

        {data && signed && (
          <Box sx={{ display: "grid", gap: 1 }}>
            {data.documents.map((slot) => (
              <Box
                key={slot.kind}
                data-testid={`agreement-${slot.kind}`}
                sx={rowSx}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 500 }}>{AGREEMENT_LABELS[slot.kind]}</Typography>
                  <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                    {slot.attached
                      ? `${slot.filename ?? "PDF"} · ${slot.byte_size ? formatBytes(slot.byte_size) : ""}${slot.uploaded_at ? ` · ${date(slot.uploaded_at)}` : ""}`
                      : slot.signed_by === "xop"
                        ? "Awaiting XOP Networks' countersignature."
                        : "Not uploaded."}
                  </Typography>
                </Box>
                <Chip
                  size="small"
                  label={slot.attached ? (slot.signed_by === "xop" ? "Countersigned" : "Signed") : "Pending"}
                  color={slot.attached ? "success" : "default"}
                  variant={slot.attached ? "filled" : "outlined"}
                />
                {slot.attached && (
                  <>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<Eye size={14} />}
                      onClick={() => onPreview(slot)}
                    >
                      Preview
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<Download size={14} />}
                      disabled={busy === slot.kind}
                      onClick={() => void download(slot)}
                    >
                      Download
                    </Button>
                  </>
                )}
              </Box>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

/* ── The file cabinet ────────────────────────────────────────────────── */

function FilesCard({
  resellerId,
  canEdit,
  onPreview,
  onError,
}: {
  resellerId: number;
  canEdit: boolean;
  onPreview: (file: CompanyFile) => void;
  onError: (message: string) => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useQuery({
    queryKey: companyDocumentKeys.files(resellerId),
    queryFn: () => listCompanyFiles(resellerId),
    enabled: resellerId > 0,
  });

  const inputRef = useRef<HTMLInputElement>(null);
  const [pendingDelete, setPendingDelete] = useState<CompanyFile | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const upload = useMutation({
    mutationFn: (file: File) => uploadCompanyFile(resellerId, file),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: companyDocumentKeys.files(resellerId) }),
    onError: (err) => onError(err instanceof Error ? err.message : "Upload failed."),
  });

  const remove = useMutation({
    mutationFn: (file: CompanyFile) => deleteCompanyFile(resellerId, file.id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: companyDocumentKeys.files(resellerId) }),
    onError: (err) => onError(err instanceof Error ? err.message : "That file could not be removed."),
  });

  const download = async (file: CompanyFile): Promise<void> => {
    setBusyId(file.id);
    try {
      await downloadToDisk(companyFileUrl(resellerId, file.id), file.name);
    } catch (err) {
      onError(err instanceof Error ? err.message : "Download failed.");
    } finally {
      setBusyId(null);
    }
  };

  const files = data?.attachments ?? [];

  return (
    <Card data-testid="files-card">
      <CardContent sx={{ display: "grid", gap: 2 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, flexWrap: "wrap" }}>
          <Box sx={{ flex: 1, minWidth: 240 }}>
            <SectionTitle
              icon={<FolderOpen size={18} />}
              title="Company files"
              hint="Paperwork XOP Networks filed for your company, and files your company added. You can remove only what your company uploaded."
            />
          </Box>
          {canEdit && (
            <>
              <input
                ref={inputRef}
                type="file"
                hidden
                data-testid="file-input"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) upload.mutate(file);
                  e.target.value = "";
                }}
              />
              <Button
                variant="contained"
                startIcon={<Upload size={14} />}
                disabled={upload.isPending}
                onClick={() => inputRef.current?.click()}
              >
                {upload.isPending ? "Uploading…" : "Upload file"}
              </Button>
            </>
          )}
        </Box>

        {isLoading && <CircularProgress size={20} />}
        {isError && <Alert severity="error">The file cabinet could not be loaded.</Alert>}

        {(data || resellerId <= 0) && files.length === 0 && (
          <Box data-testid="files-empty" sx={emptyBoxSx}>
            No files yet. Paperwork XOP Networks files for your company will appear here
            {canEdit ? ", and so will anything you upload." : "."}
          </Box>
        )}

        {files.length > 0 && (
          <Box sx={{ display: "grid", gap: 1 }}>
            {files.map((file) => (
              <Box key={file.id} data-testid="company-file" sx={rowSx}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 500 }} noWrap>
                    {file.name}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                    {formatBytes(file.size)} · {date(file.created_at)} · {uploaderLabel(file)}
                  </Typography>
                </Box>
                {isPdf(file) && (
                  <Button size="small" variant="outlined" startIcon={<Eye size={14} />} onClick={() => onPreview(file)}>
                    Preview
                  </Button>
                )}
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<Download size={14} />}
                  disabled={busyId === file.id}
                  onClick={() => void download(file)}
                >
                  Download
                </Button>
                {canEdit && file.deletable && (
                  <IconButton
                    size="small"
                    aria-label={`Remove ${file.name}`}
                    onClick={() => setPendingDelete(file)}
                    sx={{ color: "error.main" }}
                  >
                    <Trash2 size={16} />
                  </IconButton>
                )}
              </Box>
            ))}
          </Box>
        )}
      </CardContent>

      <ConfirmDialog
        isOpen={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        title="Remove this file?"
        description={pendingDelete ? `${pendingDelete.name} will be removed from your company's file cabinet.` : undefined}
        variant="destructive"
        confirmLabel="Remove"
        onConfirm={async () => {
          if (!pendingDelete) return;
          await remove.mutateAsync(pendingDelete);
          setPendingDelete(null);
        }}
      />
    </Card>
  );
}

/* ── bits ────────────────────────────────────────────────────────────── */

function SectionTitle({ icon, title, hint }: { icon: JSX.Element; title: string; hint: string }): JSX.Element {
  return (
    <Box>
      <Typography component="h2" sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: 16, fontWeight: 600 }}>
        {icon}
        {title}
      </Typography>
      <Typography sx={{ mt: 0.5, fontSize: 13, color: "text.secondary" }}>{hint}</Typography>
    </Box>
  );
}

/** crm-api already disguises the other party: XOP's uploads carry no
 * company, another reseller's are named "Another reseller". */
function uploaderLabel(file: CompanyFile): string {
  if (file.uploaded_by_kind === "staff") return "Filed by XOP Networks";
  if (file.uploaded_by_kind === "reseller") return file.uploaded_by_email ? `Uploaded by ${file.uploaded_by_email}` : "Uploaded by your company";
  return file.uploaded_by?.name ? `Filed by ${file.uploaded_by.name}` : "Filed by XOP Networks";
}

function isPdf(file: CompanyFile): boolean {
  return file.content_type === "application/pdf" || /\.pdf$/i.test(file.name);
}

const rowSx = {
  display: "flex",
  alignItems: "center",
  gap: 1.5,
  flexWrap: "wrap" as const,
  borderRadius: 1,
  border: 1,
  borderColor: "divider",
  px: 2,
  py: 1.25,
};

const emptyBoxSx = {
  borderRadius: 1,
  border: 1,
  borderColor: "divider",
  bgcolor: (t: { palette: { surfaceSoft: string } }) => alpha(t.palette.surfaceSoft, 0.4),
  p: 3,
  textAlign: "center" as const,
  fontSize: 13,
  color: "text.secondary",
};
